import { motion } from "framer-motion";
import { Compass, Radio, Telescope, Globe2 } from "lucide-react";
import { SectionHeader } from "../components/Services";
import StatsStrip from "../components/StatsStrip";
import FinalCTA from "../components/FinalCTA";

const VALUES = [
    {
        icon: Radio,
        title: "Real, not synthetic",
        desc: "Every data point comes from a verified human conversation. No bot panels.",
    },
    {
        icon: Compass,
        title: "Decision-ready",
        desc: "We design every study around the choice your team needs to make.",
    },
    {
        icon: Telescope,
        title: "Methodologically rigorous",
        desc: "Sampling, weighting and QC documented — defensible by design.",
    },
    {
        icon: Globe2,
        title: "Built for scale",
        desc: "Local interviewers in the regions that matter to your audience.",
    },
];

export default function About() {
    return (
        <main className="pt-32" data-testid="about-page">
            <section className="relative pb-12">
                <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
                <div className="pr-container relative grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <SectionHeader
                            overline="About Proksy"
                            title="We are the proxy between brands and the humans who matter."
                            subtitle="Proksy Research is a market research company specialising in CATI fieldwork, data validation, and analysis. We turn structured human conversations into decisions that move revenue, experience and policy."
                        />
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="pr-glass-strong rounded-3xl p-7 sm:p-9"
                    >
                        <p className="font-outfit text-xs uppercase tracking-[0.25em] text-blue-400 font-bold">
                            Our mission
                        </p>
                        <p className="mt-3 text-slate-300 leading-relaxed">
                            Make real human signal accessible at the speed and
                            scale that modern decisions demand — without
                            compromising on rigour.
                        </p>
                        <p className="mt-6 font-outfit text-xs uppercase tracking-[0.25em] text-emerald-300 font-bold">
                            Where we focus
                        </p>
                        <p className="mt-3 text-slate-300 leading-relaxed">
                            Our work is for brands, healthcare, finance,
                            telecom, e-commerce, government services and
                            high-growth startups — never electoral or
                            candidate research.
                        </p>
                    </motion.div>
                </div>
            </section>

            <StatsStrip />

            <section className="py-24">
                <div className="pr-container">
                    <SectionHeader
                        overline="What we stand for"
                        title="Four principles, applied to every project"
                    />
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {VALUES.map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <motion.div
                                    key={v.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.07 }}
                                    className="pr-card rounded-2xl p-6"
                                    data-testid={`about-value-${i}`}
                                >
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-400/30 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-blue-300" />
                                    </div>
                                    <h3 className="mt-5 font-outfit text-lg font-semibold text-white">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                                        {v.desc}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <FinalCTA />
        </main>
    );
}
